import {useCallback, useEffect, useRef, useState} from "react";
import CustomerSearchForm, {
    CustomerCategory, CustomerSearchFormHandles,
    CustomerSearchType
} from "../components/customer-management/CustomerSearchForm";
import {useNavigate} from "react-router-dom";
import {customerService} from "../services/customerService";
import CustomerSearchCard from "../components/customer-management/CustomerSearchCard";
import EstimateSchedulingPage from "./EstimateSchedulingPage";
import MessageCustomerModal from "../components/customer-management/MessageCustomersModal";
import LoadingSpinner from "../shared/components/loadingSpinner";


const PAGE_SIZE = 20;
const SCROLL_THRESHOLD_PX = 200;
const SEARCH_CACHE_KEY = "customerSearchCache";
const CACHE_MAX_ITEMS = 200;

function searchCacheKey(terms: CustomerSearchType, sortField: string, sortDesc: boolean) {
    return JSON.stringify({terms, sortField, sortDesc});
}

function readSearchCache() {
    try {
        const raw = sessionStorage.getItem(SEARCH_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}
const CustomerManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerms, setSearchTerms] = useState<CustomerSearchType>({});
    const [customers, setCustomers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage]                 = useState(0);
    const [loading, setLoading]           = useState(false);
    const [prefetchedCustomers, setPrefetchedCustomers] = useState<[] | null>(null);
    const [prefetching, setPrefetching] = useState(false);
    const [showMessagingModal, setShowMessagingModal]           = useState(false);
    const [searchReady, setSearchReady] = useState(false);
    const [sortField, setSortField] = useState<string>("lastName");
    const [sortDesc, setSortDesc] = useState(false);
    const formRef = useRef<CustomerSearchFormHandles>(null);
    const resultsColRef = useRef<HTMLDivElement>(null);
    const lastScrollTopRef = useRef(0);
    const pendingScrollRef = useRef<number | null>(null);
    const hydrationRef = useRef({consumed: false, key: null as string | null, page: -1});
    function removeSearchTerm (category: string, fieldKey: string) {
        const newTerms = { ...searchTerms };
        if(fieldKey === 'customerStatusName'){
            delete newTerms[category]['customerStatusId'];
        }
        if(fieldKey === 'jobStatusName'){
            delete newTerms[category]['jobStatusId'];
        }
        delete newTerms[category][fieldKey];
        setSearchTerms(newTerms);
        formRef.current?.clearField(`${fieldKey}`);
    }

    function GetSearchTermsHTML() {
        return (
            <>
                {Object.entries(searchTerms).map(([category, fieldsObj]) =>
                    Object.entries(fieldsObj!).map(([fieldKey, fieldValue]) => {
                        if(['customerStatusId', 'jobStatusId'].includes(fieldKey)) return null;
                        if(fieldKey === 'scheduledViaWeb') fieldValue = fieldValue === "true" ? "By Web" : "By Phone"
                        return (
                            <span key={`${category}-${fieldKey}`} className="searchBadge">
                                <span className="me-2">
                                    <span className="icon">
                                        <i className={"fa-regular " + (category === "job" ? "fa-file-invoice" : category === "customer" ? "fa-user" : category === "other" ? "fa-comment" : "fa-house")}/>
                                    </span>
                                    {fieldValue}
                                </span>
                                <a className="badgeClear"
                                   onClick={() => removeSearchTerm(category as CustomerCategory, fieldKey)}>
                                    <i className="fa-regular fa-circle-xmark"/>   
                                </a>
                            </span>
                        )

                    })
                )}
            </>
        );
    }

    function exportCustomers() {
        customerService.ExportCustomers(searchTerms).then(csvText => {
            const blob = new Blob([csvText], {type: "text/csv"});
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "customers‐export.csv";
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        });
    }


    const prefetchedRef = useRef<[] | null>(null);

    useEffect(() => {
        prefetchedRef.current = prefetchedCustomers;
    }, [prefetchedCustomers]);

    const searchStateRef = useRef({searchTerms, customers, totalCount, page, sortField, sortDesc});
    useEffect(() => {
        searchStateRef.current = {searchTerms, customers, totalCount, page, sortField, sortDesc};
    });

    useEffect(() => () => {
        const s = searchStateRef.current;
        if (!s.customers.length) return;
        const items = s.customers.slice(0, CACHE_MAX_ITEMS);
        const snapshot = {
            key: searchCacheKey(s.searchTerms, s.sortField, s.sortDesc),
            customers: items,
            totalCount: s.totalCount,
            page: Math.min(s.page, Math.ceil(items.length / PAGE_SIZE) - 1),
            scrollTop: lastScrollTopRef.current
        };
        try {
            sessionStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify(snapshot));
        } catch {
            sessionStorage.removeItem(SEARCH_CACHE_KEY);
        }
    }, []);

    useEffect(() => {
        if (pendingScrollRef.current == null || customers.length === 0) return;
        const scrollTop = pendingScrollRef.current;
        pendingScrollRef.current = null;
        lastScrollTopRef.current = scrollTop;
        if (resultsColRef.current) resultsColRef.current.scrollTop = scrollTop;
    }, [customers]);

    useEffect(() => {
        let cancelled = false;
        const load = async (prefetchedCustomerData) => {
            if(!searchReady) return;
            setLoading(true);
            try {
                if ((page) * PAGE_SIZE > totalCount) return;
                const result = await customerService.searchCustomers(searchTerms, page, PAGE_SIZE, sortField, sortDesc);
                if (cancelled) return;
                setTotalCount(result.totalItemsCount);
                setCustomers(prev =>
                    page === 0 ? result.items : [...prev, ...result.items]
                );

                // get next page if needed
                if ((page + 1) * PAGE_SIZE < result.totalItemsCount) {
                    setPrefetching(true);
                    const prefetch = await customerService.searchCustomers(searchTerms, page + 1, PAGE_SIZE, sortField, sortDesc);
                    if (!cancelled) setPrefetchedCustomers(prefetch.items);
                    setPrefetching(false);
                } else {
                    setPrefetchedCustomers(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        const revalidate = async (loadedCustomers) => {
            const requestCount = Math.min((page + 1) * PAGE_SIZE, CACHE_MAX_ITEMS);
            let fullRefreshDone = false;
            const lastViewedId = sessionStorage.getItem('lastViewedCustomerId');
            const lastViewed = lastViewedId
                ? loadedCustomers.find(c => String(c.customerId) === lastViewedId)
                : null;
            if (lastViewed?.customerNumber != null) {
                const recheckTerms: CustomerSearchType = {
                    ...searchTerms,
                    customer: {...(searchTerms.customer ?? {}), customerId: String(lastViewed.customerNumber)}
                };
                customerService.searchCustomers(recheckTerms, 0, PAGE_SIZE, sortField, sortDesc).then(result => {
                    if (cancelled || fullRefreshDone) return;
                    const fresh = result?.items?.find(item => String(item.customerId) === lastViewedId);
                    if (fresh) {
                        setCustomers(prev => prev.map(c => String(c.customerId) === lastViewedId ? fresh : c));
                    } else {
                        setCustomers(prev => prev.filter(c => String(c.customerId) !== lastViewedId));
                        setTotalCount(prev => Math.max(0, prev - 1));
                    }
                });
            }
            const result = await customerService.searchCustomers(searchTerms, 0, requestCount, sortField, sortDesc);
            fullRefreshDone = true;
            if (cancelled) return;
            setTotalCount(result.totalItemsCount);
            setCustomers(result.items);
        };
        if (!searchTerms || Object.keys(searchTerms).length === 0 || Object.values(searchTerms).every(inner => Object.keys(inner).length === 0)){
            setCustomers([]);
            return;
        }
        const cacheKey = searchCacheKey(searchTerms, sortField, sortDesc);
        let hydratedCustomers = null;
        if (!hydrationRef.current.consumed) {
            hydrationRef.current.consumed = true;
            const snapshot = readSearchCache();
            if (snapshot && snapshot.key === cacheKey && snapshot.customers?.length) {
                hydrationRef.current.key = cacheKey;
                hydrationRef.current.page = snapshot.page;
                pendingScrollRef.current = snapshot.scrollTop;
                setCustomers(snapshot.customers);
                setTotalCount(snapshot.totalCount);
                if (snapshot.page !== page) {
                    setPage(snapshot.page);
                    return;
                }
                hydratedCustomers = snapshot.customers;
            }
        }
        if (hydrationRef.current.key === cacheKey && hydrationRef.current.page === page) {
            revalidate(hydratedCustomers ?? customers);
            return () => { cancelled = true; };
        }
        load(prefetchedCustomers);
        return () => { cancelled = true; };
    }, [searchTerms, page, sortField, sortDesc]);

    const onSearch = (newTerms: CustomerSearchType) => {
        setSearchTerms(newTerms);
        setPage(0);
    };

    const sortOptions = [
        { field: "lastName", label: "Name" },
        { field: "city", label: "City" },
        { field: "amount", label: "Amount" },
        { field: "dateDone", label: "Schedule Date" },
        { field: "arrivalTime", label: "Arrival Time" },
    ];

    const handleSortClick = (field: string) => {
        if (field === sortField) {
            setSortDesc(!sortDesc);
        } else {
            setSortField(field);
            setSortDesc(false);
        }
        setPage(0);
    };

    const lastCustomerRef = useRef<HTMLDivElement>(null);
    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        const target = entries[0];
        if (target.isIntersecting && !loading && customers.length < totalCount) {
            if (prefetchedCustomers) {
                setCustomers(prev => [...prev, ...prefetchedCustomers]);
                setPrefetchedCustomers(null);
                setPage(prev => prev + 2);
            } else {
                setPage(prev => prev + 1); 
            }
        }
    }, [loading, customers.length, totalCount]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1,
        });
        if (lastCustomerRef.current) observer.observe(lastCustomerRef.current);
        return () => {
            if (lastCustomerRef.current) observer.unobserve(lastCustomerRef.current);
        };
    }, [handleObserver]);

    function GetCustomerCards() {
        const lastViewedCustomerId = sessionStorage.getItem('lastViewedCustomerId');
        return (
            <>
                {customers.map((customer, i) =>
                    i === customers.length - 1 ? (
                        <div ref={lastCustomerRef} key={customer.id}>
                            <CustomerSearchCard customer={customer} lastViewedCustomerId={lastViewedCustomerId} />
                        </div>
                    ) : (
                        <CustomerSearchCard key={customer.id} customer={customer} lastViewedCustomerId={lastViewedCustomerId} />
                    )
                )}
            </>
        );
    }
    return (
        <>
            <div className="container-fluid searchPage">
                <div className="row">
                    {/*<!-- LEFT COL -->*/}
                    <CustomerSearchForm onSearch={onSearch} ref={formRef} setSearchReady={() => setSearchReady(true)}/>
                    {/*<!-- RIGHT COL -->*/}
                    <div className="col-lg-9 resultsCol" ref={resultsColRef}
                         onScroll={e => lastScrollTopRef.current = e.currentTarget.scrollTop}>
                        {Object.keys(searchTerms).length > 0 && Object.values(searchTerms).some(inner => Object.keys(inner).length != 0) && (
                            <>
                                <div className="d-flex justify-content-between align-items-end mt-3">
                                    <span className="resultsFound my-3">
                                        {totalCount >= 0 ? `Found ${totalCount} results matching` : ''}
                                    </span>
                                    <div className="me-2 mb-4">
                                        <button onClick={() => setShowMessagingModal(true)} className="btn btn-outline-primary me-2 px-3">
                                            <i className="fa-regular fa-envelope"></i> Message
                                        </button>
                                        <button onClick={exportCustomers} className="btn btn-outline-primary px-3">
                                            <i className="fa-regular fa-file-export"></i> Export
                                        </button>
                                    </div>
                                </div>
        
                                <div className="searchTerms mb-3">
                                    {GetSearchTermsHTML()}
                                </div>
                                <div className="sortChips mb-3 d-flex align-items-center gap-2">
                                    <span className="text-muted" style={{fontSize: '0.85rem'}}>Sort by:</span>
                                    {sortOptions.map(opt => (
                                        <span
                                            key={opt.field}
                                            className={"sortChip" + (sortField === opt.field ? " active" : "")}
                                            onClick={() => handleSortClick(opt.field)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            {opt.label}
                                            {sortField === opt.field && (
                                                <i key={sortDesc ? "desc" : "asc"} className={"fa-solid ms-1 " + (sortDesc ? "fa-arrow-down" : "fa-arrow-up")} style={{fontSize: '0.75rem'}}/>
                                            )}
                                        </span>
                                    ))}
                                </div>
                                <div className="resultsList">
                                    {loading && <LoadingSpinner/>}
                                    {GetCustomerCards()}
                                </div>
                                {!loading && Object.keys(searchTerms).length > 0 && customers.length === 0  && (
                                    <div className="container-fluid pt-160 scanPage">
                                        <div className="messageDiv">
                                            <span className="thinky"><i className="fa-solid fa-face-thinking"></i></span>
                                            <span className="tldr">No customers found based on search results</span>
                                            <span className="message">Please try again with different search terms.</span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {(Object.keys(searchTerms).length === 0 || Object.values(searchTerms).every(inner => Object.keys(inner).length === 0)) && (
                            <div className="container-fluid pt-160 scanPage">
                                <div className="messageDiv">
                                    <span className="thinky"><i className="fa-regular fa-circle-info"></i></span>
                                    <span className="message">Please use the form to search for customers</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <MessageCustomerModal searchTerms={searchTerms} show={showMessagingModal}
                                  toggle={() => setShowMessagingModal(false)} totalCustomerCount={totalCount}
                                  customers={customers}/>
        </>
    );
}

export default CustomerManagementPage